import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { filter, pick } from 'ramda';
import { useSnackbar } from 'notistack';
import { RouteComponentProps } from 'react-router-dom';
import catchOnly from 'utils/catchOnly';
import isPresent from 'utils/isPresent';

import * as Yup from 'yup';
import {
  composeSchema,
  isPhoneNumber,
  isSsn,
  isZipCode,
} from 'utils/validations';

import FmcForm from 'components/FmcForm';
import LookupFormView from '../../LookupFormView';

import useOTPSender, { OTPSenderError } from 'hooks/requests/otpSender';

import { NonClientError } from 'hooks/requests/fetchUtils';
import useNavigationHelpers from 'hooks/useNavigationHelpers';

import {
  mergeApplication,
  selectCreditApplication,
} from 'state/creditApplication';

import useFieldCustomization from 'hooks/useFieldCustomization';

export interface FormValues {
  mobilePhone: string;
  ssn: string;
  zip: string;
}

type PropTypes = RouteComponentProps & {
  onSubmit: (values: FormValues) => void;
};

const LookupForm: React.FC<PropTypes> = props => {
  const { isLastFour } = useFieldCustomization();
  const isLastFourBool = isLastFour();
  const [sendOTP] = useOTPSender();
  const { onSubmit: onSubmitSetValues, match } = props;

  const { push } = useNavigationHelpers();
  const { enqueueSnackbar } = useSnackbar();

  const dispatch = useDispatch();
  const creditApplication = useSelector(selectCreditApplication);
  const schema = useMemo(
    () =>
      Yup.object().shape({
        mobilePhone: composeSchema(
          Yup.string().label('Mobile #'),
          isPhoneNumber,
          Yup.string().required(),
        ),

        ssn: Yup.string()
          .label(isLastFourBool ? 'Last 4 SSN' : 'SSN')
          .concat(isSsn(isLastFourBool))
          .required(),

        zip: composeSchema(
          Yup.string().label('ZIP Code'),
          Yup.string().required(),
          isZipCode,
        ),
      }),
    [isLastFourBool],
  );

  const onSubmit = useCallback(
    async (rawValues: FormValues) => {
      const values = schema.cast(rawValues);

      // Merge zip for use with address typeahead feature.
      dispatch(
        mergeApplication({
          ssn: values.ssn,
          mobilePhone: values.mobilePhone,
          zip: values.zip,
        }),
      );

      try {
        await sendOTP({ phone: values.mobilePhone as string });
        onSubmitSetValues(values);
        push(`${match.path}/otp-verification`.replace('//', '/'));
      } catch (e) {
        if (catchOnly(e, NonClientError, OTPSenderError)) {
          enqueueSnackbar('Something has gone wrong. Please try again later.', {
            variant: 'error',
          });
        }
      }
    },
    [
      enqueueSnackbar,
      push,
      sendOTP,
      schema,
      dispatch,
      onSubmitSetValues,
      match,
    ],
  );

  const initialValues: FormValues = {
    mobilePhone: '',
    ssn: '',
    zip: '',
  };

  const prefillValues = filter(
    isPresent,
    pick(['mobilePhone', 'ssn', 'zip'], creditApplication),
  );

  return (
    <FmcForm
      initialValues={{ ...initialValues, ...prefillValues }}
      onSubmit={onSubmit}
      validationSchema={schema}
    >
      {formProps => <LookupFormView {...formProps} />}
    </FmcForm>
  );
};

export default LookupForm;
